/**
 * Compact "relationship chip" shown in the gap between the 3D viewport
 * (top 60dvh) and the chat area (bottom). Per the 2026-05-23 UX redo:
 *
 *   - NO raw score number (gamification + injection risk)
 *   - NO "第 N 階" label (meaningless without context)
 *   - Just tier-name + an info marker; tap opens an explainer modal
 *   - Positioned away from the hotkey FAB / status pill zone
 *
 * Refresh policy: fetch on mount + on window focus. No periodic
 * polling — the platform analyzer only updates affinity hourly, so
 * polling every 30s was wasted bandwidth. If the parent unmounts and
 * remounts (mode toggle / route change), that triggers a fresh fetch.
 *
 * Failure behavior: silent. If the API 4xx's (non-fanMode character,
 * fan signed out mid-session, etc.) the chip simply doesn't render —
 * never crash the embed because of an enrichment view.
 */
import { useEffect, useState } from 'react';
import { getMyRelationship } from '../../api/fan-api';
import { resolveTier } from '@nxorbit/shared-schema';

interface AffinityBarProps {
  characterId: string;
  characterName?: string;
  /** Set true to skip rendering (anonymous embed, fanMode disabled, etc). */
  hidden?: boolean;
}

interface ResolvedState {
  tier: number;
  name: string;
  description: string;
}

/** localStorage key remembering the last tier the fan SAW for this
 *  character — a higher tier on the next fetch triggers the level-up
 *  celebration exactly once. */
function tierSeenKey(characterId: string): string {
  return `nx:affinity-tier-seen:${characterId}`;
}

export function AffinityBar({ characterId, characterName, hidden }: AffinityBarProps) {
  const [state, setState] = useState<ResolvedState | null>(null);
  const [showModal, setShowModal] = useState(false);
  // Tier-up celebration (particles + banner); auto-clears after 2.8s.
  const [celebrate, setCelebrate] = useState<{ name: string } | null>(null);

  useEffect(() => {
    if (!celebrate) return;
    const t = setTimeout(() => setCelebrate(null), 2800);
    return () => clearTimeout(t);
  }, [celebrate]);

  useEffect(() => {
    if (hidden || !characterId) return;
    let alive = true;
    const applyResolved = (next: ResolvedState) => {
      setState(next);
      // Level-up detection: previously-seen lower tier → celebrate.
      // First sighting just records (no fanfare for the initial load).
      try {
        const raw = window.localStorage.getItem(tierSeenKey(characterId));
        const prev = raw === null ? null : Number(raw);
        if (prev !== null && Number.isFinite(prev) && next.tier > prev) {
          setCelebrate({ name: next.name });
        }
        window.localStorage.setItem(tierSeenKey(characterId), String(next.tier));
      } catch { /* private mode etc. — celebration is enrichment */ }
    };
    const fetchOnce = async () => {
      try {
        const detail = await getMyRelationship(characterId);
        if (!alive) return;
        // Prefer server-resolved fields (carry operator's per-character
        // tier name + description override). Fall back to local
        // resolveTier with platform defaults if the server isn't
        // up-to-date yet — keeps the bar working across version skew.
        if (typeof detail.tierV2 === 'number' && detail.tierV2Name) {
          applyResolved({
            tier: detail.tierV2,
            name: detail.tierV2Name,
            description: detail.tierV2Description ?? '',
          });
        } else if (typeof detail.value === 'number') {
          const r = resolveTier(detail.value, null);
          applyResolved({ tier: r.tier, name: r.name, description: r.description });
        }
      } catch {
        // Silent — affinity is enrichment, not core UX.
      }
    };
    void fetchOnce();
    const onFocus = () => { void fetchOnce(); };
    const onAffinityChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ characterId?: string }>).detail;
      if (detail?.characterId !== characterId) return;
      void fetchOnce();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('nx-affinity:changed', onAffinityChanged);
    return () => {
      alive = false;
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('nx-affinity:changed', onAffinityChanged);
    };
  }, [characterId, hidden]);

  if (hidden || state === null) return null;

  const { tier, name: tierName, description: tierDesc } = state;

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        aria-label={`你和此角色目前的關係：${tierName}（點擊了解）`}
        className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-slate-900/60 px-3 py-1 text-[11px] font-medium text-slate-100 ring-1 ring-white/15 backdrop-blur-md shadow-sm transition hover:bg-slate-900/80"
      >
        <span className="nx-affinity-heart" aria-hidden="true">💗</span>
        <span className="truncate">{tierName}</span>
        <span className="text-[10px] text-slate-400" aria-hidden="true">ⓘ</span>
      </button>
      {celebrate && <TierUpCelebration tierName={celebrate.name} />}
      {showModal && (
        <RelationshipExplainerModal
          characterId={characterId}
          characterName={characterName}
          tier={tier}
          tierName={tierName}
          tierDescription={tierDesc}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

/** Tier-up burst: ~14 particles on ballistic arcs (rise, drift, fall —
 *  see nx-tierup-particle keyframes) around a springy banner. Pure
 *  presentation; the parent unmounts it after 2.8s. */
function TierUpCelebration({ tierName }: { tierName: string }) {
  const PARTICLES = ['💗', '✨', '💖', '⭐', '💞', '✨', '💗', '🌟', '💖', '✨', '💕', '⭐', '💗', '✨'];
  return (
    <div className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center" aria-live="polite">
      <div className="relative">
        {PARTICLES.map((p, i) => {
          // Deterministic fan-out by index: spread angles across a full
          // circle with slight stagger, alternating arc heights.
          const angle = (i / PARTICLES.length) * Math.PI * 2;
          const dist = 70 + (i % 3) * 38;
          const dx = Math.round(Math.cos(angle) * dist);
          const up = -(50 + (i % 4) * 22);
          const down = 55 + ((i * 7) % 50);
          const rot = ((i % 2 === 0 ? 1 : -1) * (120 + (i * 31) % 160));
          return (
            <span
              key={i}
              className="nx-tierup-particle text-xl"
              style={{
                ['--dx' as string]: `${dx}px`,
                ['--up' as string]: `${up}px`,
                ['--down' as string]: `${down}px`,
                ['--rot' as string]: `${rot}deg`,
                ['--delay' as string]: `${(i % 5) * 0.05}s`,
              }}
              aria-hidden="true"
            >{p}</span>
          );
        })}
        <div className="nx-tierup-banner rounded-2xl bg-slate-950/85 px-6 py-4 text-center ring-1 ring-pink-400/40 shadow-[0_0_40px_-8px_rgba(244,114,182,0.5)] backdrop-blur-md">
          <div className="text-[11px] uppercase tracking-[0.22em] text-pink-300/80">關係升級</div>
          <div className="mt-1 text-lg font-bold text-slate-50">💗 {tierName}</div>
        </div>
      </div>
    </div>
  );
}

interface ExplainerProps {
  characterId: string;
  characterName?: string;
  tier: number;
  tierName: string;
  tierDescription: string;
  onClose: () => void;
}

function RelationshipExplainerModal({
  characterId,
  characterName,
  tier,
  tierName,
  tierDescription: description,
  onClose,
}: ExplainerProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-slate-900 p-5 ring-1 ring-white/15 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
            你與此角色的關係
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="關閉"
            className="-mr-1 -mt-1 flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition hover:bg-white/10 hover:text-slate-200"
          >
            ×
          </button>
        </div>

        <div className="mt-3 flex items-center gap-3">
          <span className="text-3xl" aria-hidden="true">💗</span>
          <div className="flex flex-col">
            <span className="text-xl font-bold text-slate-50">{tierName}</span>
            <span className="text-[11px] text-slate-500">階段 {tier} / 10</span>
          </div>
        </div>

        <p className="mt-4 text-[13px] leading-relaxed text-slate-300">
          {description}
        </p>
        <p className="mt-3 text-[12px] leading-relaxed text-slate-400">
          {`會隨著你和 ${characterName ?? '此角色'} 的互動慢慢改變 — 多聊聊、表達真誠的感受，他會慢慢更認識你。系統每小時會根據最新的對話自動更新。`}
        </p>

        <div className="mt-5 flex items-center justify-end gap-2">
          <a
            href={`/relationships/${characterId}`}
            className="rounded-full bg-blue-500 px-4 py-1.5 text-[12px] font-medium text-white shadow-sm transition hover:bg-blue-400"
          >
            查看完整關係
          </a>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-white/10 px-4 py-1.5 text-[12px] font-medium text-slate-100 transition hover:bg-white/15"
          >
            關閉
          </button>
        </div>
      </div>
    </div>
  );
}
